import { CurrencyCode } from '@data/shopify/storefront/types'
import { Cart } from '@lib/cart/types'
import { getPriceNumber } from '@lib/helpers'
import { Locale } from '@lib/language'
import { LanguageContext } from '@lib/language-context'
import { getFormattedProductTitle } from '@lib/product'
import { ProductContext } from '@lib/product-context'
import { getPageLocation } from '@lib/routes'
import { ShopContext } from '@lib/shop-context'
import { SiteContext } from '@lib/site-context'
import {
  ShopifyAddToCart,
  AnalyticsEventName as ShopifyAnalyticsEventName,
  ShopifyPageView,
  getClientBrowserParameters,
  sendShopifyAnalytics,
} from '@shopify/hydrogen-react'
import { diff } from 'jsondiffpatch'
import { customAlphabet } from 'nanoid'
import { useContext, useEffect, useRef } from 'react'
import { AnalyticsContext } from './context'
import { triggerKlaviyoViewProductEvent } from './klaviyo'
import {
  AnalyticsEvent,
  AnalyticsEventName,
  AnalyticsEventType,
  AnalyticsSettings,
  EventPayload,
  GoogleProductEventPayload,
  MetaProductEventPayload,
  ProductVariantEventInput,
  ShopifyEventPayload,
} from './types'

const shopifyEventNames: AnalyticsEventName[] = [
  AnalyticsEventName.AddToCart,
  AnalyticsEventName.Pageview,
]

/**
 * Gets price number formatted for analytics events.
 */
const getAnalyticsPriceNumber = (minorUnits: number) => {
  // Convert price from minor to major units
  const price = minorUnits / Math.pow(10, 2)

  const priceString = price.toLocaleString('en', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  return Number(priceString)
}

/**
 * Finds an analytics event that is ready to be processed.
 */
export const getReadyAnalyticsEvent = (
  events: AnalyticsEvent[],
  shopId?: string,
  currencyCode?: CurrencyCode,
  cartId?: string,
) => {
  return events.find((event) => {
    if (event.type === AnalyticsEventType.GoogleTagManager) {
      return true
    }

    if (event.type === AnalyticsEventType.Shopify) {
      if (!shopId || !currencyCode) {
        return false
      }

      if (event.name === AnalyticsEventName.AddToCart && !cartId) {
        return false
      }

      return true
    }
  })
}

/**
 * Creates custom analytics events based on event name.
 */
export const getAnalyticsEvents = (
  eventName: AnalyticsEventName,
  eventPayload: EventPayload,
  settings: AnalyticsSettings,
) => {
  const nanoid = customAlphabet('1234567890abcdef', 16)

  const analyticsEvents: AnalyticsEvent[] = []

  if (settings.googleEvents) {
    analyticsEvents.push({
      id: nanoid(),
      type: AnalyticsEventType.GoogleTagManager,
      name: eventName,
      eventPayload: {
        // TODO: Replace pageTitle, pagePath data layer variables with page_title, page_location, page_referrer.
        pagePath: window.location.pathname,
        pageTitle: document.title,
        pageLocation: getPageLocation(),
        ...eventPayload,
      },
    })
  }

  if (settings.shopifyEvents && shopifyEventNames.includes(eventName)) {
    const clientBrowserParameters = getClientBrowserParameters()

    analyticsEvents.push({
      id: nanoid(),
      type: AnalyticsEventType.Shopify,
      name: eventName,
      eventPayload: {
        ...clientBrowserParameters,
        ...eventPayload,
      },
    })
  }

  return analyticsEvents
}

/**
 * Triggers an event in Google Tag Manager.
 */
export const triggerGoogleTagManagerEvent = (
  eventName: AnalyticsEventName,
  eventPayload?: EventPayload,
) => {
  window.dataLayer = window.dataLayer || []

  const nanoid = customAlphabet('1234567890abcdef', 16)

  let dataLayerEvent: EventPayload = {
    event: eventName,
    eventId: nanoid(),
  }

  if (eventPayload) {
    dataLayerEvent = {
      ...dataLayerEvent,
      ...eventPayload,
    }
  }

  // Push empty ecommerce before events with ecommerce
  if (eventPayload && 'ecommerce' in eventPayload) {
    window.dataLayer.push({
      ecommerce: null,
    })
  }

  window.dataLayer.push(dataLayerEvent)
}

/**
 * Triggers an event in Shopify.
 */
export const triggerShopifyEvent = async (
  eventName: AnalyticsEventName,
  shopifyEventPayload: ShopifyEventPayload,
  shopId?: string | null,
  currency?: CurrencyCode,
  cartId?: string,
  shopDomain?: string,
) => {
  switch (eventName) {
    case AnalyticsEventName.Pageview: {
      if (!shopId || !currency) {
        break
      }

      const event: ShopifyPageView = {
        eventName: ShopifyAnalyticsEventName.PAGE_VIEW,
        payload: {
          ...shopifyEventPayload,
          shopId,
          currency,
          // TODO: Use user consent from Cookiebot.
          hasUserConsent: true,
          ccpaEnforced: false,
          gdprEnforced: false,
          analyticsAllowed: true,
          marketingAllowed: true,
          saleOfDataAllowed: true,
        },
      }
      await sendShopifyAnalytics(event, shopDomain)

      break
    }

    case AnalyticsEventName.AddToCart: {
      if (!shopId || !currency || !cartId) {
        break
      }

      const event: ShopifyAddToCart = {
        eventName: ShopifyAnalyticsEventName.ADD_TO_CART,
        payload: {
          ...shopifyEventPayload,
          shopId,
          currency,
          cartId,
          // TODO: Use user consent from Cookiebot.
          hasUserConsent: true,
          ccpaEnforced: false,
          gdprEnforced: false,
          analyticsAllowed: true,
          marketingAllowed: true,
          saleOfDataAllowed: true,
        },
      }
      await sendShopifyAnalytics(event, shopDomain)

      break
    }
  }
}

/**
 * Gets product-related analytics event payload.
 */
export const getProductEventPayload = (
  productTitle: string,
  variants: ProductVariantEventInput[],
  currencyCode?: CurrencyCode,
) => {
  if (!currencyCode) {
    return
  }

  const googleProductEventPayload: GoogleProductEventPayload = {
    items: variants.map((variant, index) => {
      const variantOptionString = variant.options
        ?.map((option) => option.value)
        ?.join(', ')
      const variantDiscount = variant.comparePrice
        ? variant.comparePrice - variant.price
        : 0

      return {
        index,
        item_id: `${variant.variantId}`,
        item_name: productTitle,
        item_variant: variantOptionString,
        price: getAnalyticsPriceNumber(variant.price),
        discount: getAnalyticsPriceNumber(variantDiscount),
        quantity: variant.quantity,
      }
    }),
  }

  const metaProductEventPayload: MetaProductEventPayload = {
    content_type: 'product',
    content_name: productTitle,
    content_ids: variants.map((variant) => `${variant.variantId}`),
    contents: variants.map((variant) => {
      return {
        id: `${variant.variantId}`,
        quantity: variant.quantity,
      }
    }),
  }

  const value = variants.reduce((total, variant) => {
    return total + variant.price * variant.quantity
  }, 0)
  const eventPayload: EventPayload = {
    ...googleProductEventPayload,
    ...metaProductEventPayload,
    value: getAnalyticsPriceNumber(value),
    currency: currencyCode,
    ecommerce: {
      value: getAnalyticsPriceNumber(value),
      currency: currencyCode,
    },
  }

  return eventPayload
}

/**
 * Gets initiate checkout analytics event data.
 */
export const getInitiateCheckoutEventPayload = (
  locale: Locale,
  cart: Cart,
  currencyCode?: CurrencyCode,
) => {
  const eventPayload: EventPayload = {
    currency: currencyCode,
    value: getPriceNumber(cart.subTotal),
    items: cart.lineItems.map((lineItem, index) => ({
      index,
      item_id: lineItem.variantID,
      item_name: getFormattedProductTitle(locale, lineItem.product.title),
      item_variant: lineItem.options?.map((option) => option.value)?.join(', '),
      price: getPriceNumber(lineItem.price),
      quantity: lineItem.quantity,
    })),
    variant_ids: cart.lineItems.map((lineItem) =>
      lineItem.variantID.toString(),
    ),
  }

  if (cart.automaticDiscount?.amount) {
    eventPayload.automatic_discount = {
      title: cart.automaticDiscount.title ?? '',
      amount: cart.automaticDiscount.amount / 100,
    }
  }

  return eventPayload
}

/**
 * Returns hook that triggers view product event on page load/navigation and active variant change.
 */
export const useViewProductEvent = () => {
  const { triggerAnalyticsEvent } = useContext(AnalyticsContext)
  const { locale } = useContext(LanguageContext)
  const { product, activeVariant } = useContext(ProductContext)
  const { settings } = useContext(SiteContext)
  const { currencyCode } = useContext(ShopContext)

  const lastProductPageviewEventRef = useRef<EventPayload | undefined>(
    undefined,
  )

  useEffect(() => {
    if (!product || !activeVariant || !currencyCode) {
      return
    }

    const eventPayload = getProductEventPayload(
      getFormattedProductTitle(locale, product.title),
      [
        {
          variantId: activeVariant.variantID,
          options: activeVariant.options,
          price: activeVariant.price,
          comparePrice: activeVariant.comparePrice,
          quantity: 1,
        },
      ],
      currencyCode,
    )

    // Make sure not to send the same event payload mulitple times
    const eventDifference = diff(
      lastProductPageviewEventRef.current,
      eventPayload,
    )

    if (!eventDifference) {
      return
    }

    // Trigger view product event
    triggerAnalyticsEvent(AnalyticsEventName.ViewProduct, eventPayload)

    triggerKlaviyoViewProductEvent(locale, settings, product, activeVariant)

    lastProductPageviewEventRef.current = eventPayload
  }, [
    activeVariant,
    currencyCode,
    locale,
    product,
    settings,
    triggerAnalyticsEvent,
  ])
}
